// react_frontend/src/Components/Modules/Billing/EditCustomer.js
import React, { useState, useEffect, useRef } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Schema } from "rsuite";
// RSuite Components
import {
  Form,
  Button,
  Input,
  Message,
  Loader,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaUser,
  FaPhone,
  FaEdit,
  FaList,
  FaSave,
} from "react-icons/fa";

const EditCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState({
    customer_name: "",
    customer_phone: "",
  });
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // Validation Schema
  const { StringType } = Schema.Types;

  const model = Schema.Model({
    customer_name: StringType().isRequired("Customer name is required."),
    customer_phone: StringType()
      .addRule((value) => /^\d{6,15}$/.test(value), "Phone must be 6–15 digits.")
      .isRequired("Phone number is required."),
  });

  // Fetch Customer Data
  useEffect(() => {
    const fetchCustomer = async () => {
      setLoading(true);
      try {
        const response = await AxiosAuthInstance.get(`add_customer/${id}`);
        const customer = response.data.customer || {};

        setFormValue({
          customer_name: customer.name || "",
          customer_phone: customer.phone || "",
          customer_email: customer.email || "",
          customer_address: customer.address || "",
          vat_number: customer.vat_number || "",
          tax_number: customer.tax_number || "",
          cr_number: customer.cr_number || "",
        });
      } catch (error) {
        console.error("Failed to fetch customer:", error);
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to load customer data.
          </Message>,
          { placement: "topEnd" }
        );
        navigate("/customers");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id, navigate]);

  // Handle Form Submit
  const handleSubmit = async () => {
    if (!formRef.current.check()) return;

    setLoading(true);
    try {
      const res = await AxiosAuthInstance.post(`edit_customer/${id}`, formValue);

      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Customer updated successfully!
          </Message>,
          { placement: "topEnd" }
        );
        navigate("/customers");
      } else {
        toaster.push(
          <Message type="error" duration={5000}>
            {res.data.message || "Failed to update customer."}
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      setFormError(formattedErrors);

      toaster.push(
        <Message type="error" duration={5000}>
          {serverErrors.general?.[0] || "Failed to update customer."}
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/customers");
  };

  return (
    <>
      <Breadcrumb title="Edit Customer" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaEdit /> Edit Customer
            </h5>
            <Link to="/customers" className="text-white text-decoration-none">
              <Button
                size="sm"
                appearance="subtle"
                className="text-white"
                startIcon={<FaList />}
              >
                Customer List
              </Button>
            </Link>
          </div>

          {/* Body */}
          <div className="card-body bg-light p-4">
            {loading ? (
              <div className="d-flex justify-content-center my-5">
                <Loader size="md" /> <span className="ms-2">Loading customer...</span>
              </div>
            ) : (
              <Form
                ref={formRef}
                fluid
                formValue={formValue}
                formError={formError}
                onChange={setFormValue}
                onCheck={setFormError}
                model={model}
              >
                <Form.Group controlId="customer_name">
                  <Form.ControlLabel>Customer Name *</Form.ControlLabel>
                  <Form.Control name="customer_name" placeholder="Enter full name" prefix={<FaUser />} />
                  <Form.ErrorMessage name="customer_name" />
                </Form.Group>

                <Form.Group controlId="customer_phone">
                  <Form.ControlLabel>Phone Number *</Form.ControlLabel>
                  <Form.Control name="customer_phone" type="tel" placeholder="e.g., 01712345678" prefix={<FaPhone />} />
                  <Form.ErrorMessage name="customer_phone" />
                </Form.Group>

                <Form.Group controlId="customer_email">
                  <Form.ControlLabel>Email Address</Form.ControlLabel>
                  <Form.Control name="customer_email" type="email" placeholder="Enter email address" />
                  <Form.ErrorMessage name="customer_email" />
                </Form.Group>

                <Form.Group controlId="customer_address">
                  <Form.ControlLabel>Address</Form.ControlLabel>
                  <Form.Control name="customer_address" as="textarea" rows={3} placeholder="Enter full address" />
                  <Form.ErrorMessage name="customer_address" />
                </Form.Group>

                <div className="row">
                  <div className="col-md-4">
                    <Form.Group controlId="vat_number">
                      <Form.ControlLabel>VAT / BIN No</Form.ControlLabel>
                      <Form.Control name="vat_number" placeholder="VAT Number" />
                      <Form.ErrorMessage name="vat_number" />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group controlId="tax_number">
                      <Form.ControlLabel>Tax / TIN No</Form.ControlLabel>
                      <Form.Control name="tax_number" placeholder="Tax Number" />
                      <Form.ErrorMessage name="tax_number" />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group controlId="cr_number">
                      <Form.ControlLabel>CR / Trade License</Form.ControlLabel>
                      <Form.Control name="cr_number" placeholder="CR Number" />
                      <Form.ErrorMessage name="cr_number" />
                    </Form.Group>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-3 pt-3 border-top" style={{ marginTop: "2rem" }}>
                  <Button
                    appearance="primary"
                    color="blue"
                    size="md"
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{ flex: 1 }}
                    startIcon={<FaSave />}
                  >
                    {loading ? (
                      <>
                        <Loader size="sm" /> Updating...
                      </>
                    ) : (
                      "Update Customer"
                    )}
                  </Button>

                  <Button
                    appearance="subtle"
                    color="red"
                    size="md"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditCustomer;